import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from 'services'
import type { Product } from 'types'

type SetProductDefaultTagRequest = {
  product_id: string
  tag_shop_product_id: string
}

type RemoveProductDefaultTagRequest = {
  product_id: string
}

type ProductDefaultTagResponse = {
  product: Product
}

/**
 * Turn a FAILED default-tag call into a rejected promise carrying a readable reason.
 *
 * `services`' shared `errorHandler` RESOLVES an HTTP error with `error.response` instead of
 * rethrowing it, so `api.put`/`api.delete` never reject on a 4xx/5xx. Without this check the
 * mutation reported SUCCESS with the error body as its data, `onSuccess` then read
 * `data.product.id` off a body that has no `product`, and the resulting
 * "Cannot read properties of undefined (reading 'id')" was caught by react-query and shown to
 * the user as the error message — a raw TypeError in place of the reason the server actually
 * gave. Rejecting here means `onError` receives the real message.
 */
const unwrapDefaultTagResponse = (response: any): ProductDefaultTagResponse => {
  const status: number = response?.status ?? 0
  const body = response?.data
  if (status >= 200 && status < 300 && body?.product) return body as ProductDefaultTagResponse

  const serverMessage = body?.message
  const message = Array.isArray(serverMessage) ? serverMessage[0] : serverMessage
  throw new Error(
    typeof message === 'string' && message
      ? message
      : 'Could not update the default tag. Please try again.',
  )
}

// API functions
const setProductDefaultTag = async (request: SetProductDefaultTagRequest): Promise<ProductDefaultTagResponse> => {
  const response = await api.put(`/products/admin/${request.product_id}/default-tag`, {
    tag_shop_product_id: request.tag_shop_product_id
  })
  return unwrapDefaultTagResponse(response)
}

const removeProductDefaultTag = async (request: RemoveProductDefaultTagRequest): Promise<ProductDefaultTagResponse> => {
  const response = await api.delete(`/products/admin/${request.product_id}/default-tag`)
  return unwrapDefaultTagResponse(response)
}

// Mutation hooks
type UseProductDefaultTagMutationsOptions = {
  onSuccess?: (data: ProductDefaultTagResponse) => void
  onError?: (error: Error) => void
}

export const useProductDefaultTagMutations = (options?: UseProductDefaultTagMutationsOptions) => {
  const queryClient = useQueryClient()

  const setDefaultTagMutation = useMutation<
    ProductDefaultTagResponse,
    Error,
    SetProductDefaultTagRequest
  >({
    mutationFn: setProductDefaultTag,
    onSuccess: (data) => {
      // Invalidate products queries to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['products'] })
      // Optional-chained: a cache invalidation must never be the thing that throws. When it did,
      // react-query routed the TypeError into `onError` and the UI reported a successful write
      // as a failure.
      if (data?.product?.id) {
        queryClient.invalidateQueries({ queryKey: ['products', 'detail', data.product.id] })
      }

      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const removeDefaultTagMutation = useMutation<
    ProductDefaultTagResponse,
    Error,
    RemoveProductDefaultTagRequest
  >({
    mutationFn: removeProductDefaultTag,
    onSuccess: (data) => {
      // Invalidate products queries to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['products'] })
      // Optional-chained: a cache invalidation must never be the thing that throws. When it did,
      // react-query routed the TypeError into `onError` and the UI reported a successful write
      // as a failure.
      if (data?.product?.id) {
        queryClient.invalidateQueries({ queryKey: ['products', 'detail', data.product.id] })
      }

      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    setDefaultTag: setDefaultTagMutation.mutate,
    setDefaultTagAsync: setDefaultTagMutation.mutateAsync,
    removeDefaultTag: removeDefaultTagMutation.mutate,
    removeDefaultTagAsync: removeDefaultTagMutation.mutateAsync,
    isSettingTag: setDefaultTagMutation.isPending,
    isRemovingTag: removeDefaultTagMutation.isPending,
    isLoading: setDefaultTagMutation.isPending || removeDefaultTagMutation.isPending,
    setTagError: setDefaultTagMutation.error,
    removeTagError: removeDefaultTagMutation.error,
    setTagData: setDefaultTagMutation.data,
    removeTagData: removeDefaultTagMutation.data,
    resetSetTag: setDefaultTagMutation.reset,
    resetRemoveTag: removeDefaultTagMutation.reset,
  }
}